import { useState, useEffect } from 'react';
import { Sort } from 'src/icons/Sort.icon';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown, Menu, Select } from 'antd';
import {
  ActionMenuIcon,
  BinIcon,
  EditAppointmentIcon,
  PlusIcon,
} from 'src/icons';
import { SuperAdminTable } from '../../../../baseComponents/Table';
import { Button } from '../../../../baseComponents';
import {
  listKnowledgebaseOverridesAction,
  getKnowledgebaseOverridesAction,
} from '../../../../../store/actions/knowledgebaseoverrides';
import { getAllSpecialtyListAction } from 'src/modules/SuperAdmin/store/actions/specialty.action';
import AddNewKnowledgeBaseOverrides from './AddNewKnowledgeBaseOverrides';
import EditKnowledgeBaseOverrides from './EditKnowledgeBaseOverrides';
import DeleteKnowledgeBaseOverrides from './DeleteKnowledgeBaseOverrides';

import { getFromStorage } from 'src/lib/storage';
import organizationEventEmitter from 'src/utilities/organizationEmmiter';
import { getAllActiveOrganizationAction } from 'src/modules/SuperAdmin/store/actions/organization.action';

export const KnowledgeBaseOverridesPage = () => {
  const { knowledgebaseOverridesList, knowledgebaseOverridesLoading } =
    useSelector((state) => state?.knowledgebaseState);
  const { specialties } = useSelector((state) => ({
    specialties: state?.specialtyState?.getAllSpecialtiesList,
  }));
  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );

  const [tableData, setTableData] = useState(null);
  const [knowledgebaseModalOpen, setKnowledgebaseModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isfilterOn, setIsFilterOn] = useState(false);

  const [editingKnowledgebaseId, setEditingKnowledgebaseId] = useState(null);
  const [deletingKnowledgebaseId, setDeletingKnowledgebaseId] = useState(null);

  // Organization filtering state
  const [selectedOrganizationUUID, setSelectedOrganizationUUID] = useState(
    () => {
      const savedOrg = getFromStorage('superAdminSelectedOrganization');
      const parsedSavedOrg = savedOrg ? JSON.parse(savedOrg) : null;
      return parsedSavedOrg?.org_uuid || currentPractitioner?.org_uuid || null;
    }
  );

  const dispatch = useDispatch();

  useEffect(() => {
    if (selectedOrganizationUUID) {
      dispatch(listKnowledgebaseOverridesAction(selectedOrganizationUUID));
      dispatch(getAllSpecialtyListAction());
      dispatch(getAllActiveOrganizationAction());
    }
  }, [dispatch, selectedOrganizationUUID]);

  // Listen for organization changes
  useEffect(() => {
    const unsubscribe = organizationEventEmitter.subscribe(
      'organizationChanged',
      (newOrg) => {
        setSelectedOrganizationUUID(newOrg?.org_uuid);
      }
    );
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (knowledgebaseOverridesList?.knowledge_base_overrides?.length > 0) {
      let dataArray = knowledgebaseOverridesList?.knowledge_base_overrides?.map(
        (list, index) => {
          return {
            key: index,
            knowledge_base_id: list?.knowledge_base_id,
            specialty_id: list?.specialty_id,
            specialty_name: list?.specialty_name,
            encounter_phase: list?.encounter_phase,
            encounter_type: list?.encounter_type,
            content: list?.content,
          };
        }
      );
      if (dataArray && Array.isArray(dataArray) && dataArray.length > 0) {
        setTableData(dataArray);
      }
    } else {
      setTableData([]);
    }
  }, [knowledgebaseOverridesList?.knowledge_base_overrides]);

  const handleEditKnowledgeBase = (knowledgebaseId) => {
    setEditingKnowledgebaseId(knowledgebaseId);
    setEditModalOpen(true);
  };

  const handleDeleteKnowledgeBase = (knowledgebaseId) => {
    setDeletingKnowledgebaseId(knowledgebaseId);
    setDeleteModalOpen(true);
  };

  const menuOptions = (id, record) => {
    return (
      <Menu
        onClick={({ key }) => {
          if (key === 'editKnowledgeBase') {
            handleEditKnowledgeBase(record?.knowledge_base_id);
          }

          if (key === 'deleteKnowledgeBase') {
            handleDeleteKnowledgeBase(record?.knowledge_base_id);
          }
        }}
      >
        <Menu.Item key="editKnowledgeBase">
          <div className="dropdown-text flex items-center space-x-2">
            <EditAppointmentIcon /> <span>Edit knowledge base override</span>
          </div>
        </Menu.Item>
        <Menu.Item key="deleteKnowledgeBase">
          <div className="dropdown-text flex items-center space-x-2">
            <BinIcon /> <span>Remove knowledge base override</span>
          </div>
        </Menu.Item>
      </Menu>
    );
  };

  const specialtyNameSorter = (a, b) => {
    const nameA = a.specialty_name || '';
    const nameB = b.specialty_name || '';
    return nameA.localeCompare(nameB);
  };

  const onEdit = (knowledgebaseId) => {
    setEditingKnowledgebaseId(knowledgebaseId);
    setEditModalOpen(true);
  };

  const onDelete = (knowledgebaseId) => {
    setDeletingKnowledgebaseId(knowledgebaseId);
    setDeleteModalOpen(true);
  };

  const columns = [
    {
      title: (
        <span className="font-[700]">
          {' '}
          Specialty name{' '}
          {tableData?.length > 0 && <Sort className={'sort-icon-doctor'} />}
        </span>
      ),
      dataIndex: 'specialty_name',
      key: 'specialty_name',
      sorter: tableData?.length > 0 ? specialtyNameSorter : false,
    },
    {
      title: <span className="font-[700]"> Encounter Phase </span>,
      dataIndex: 'encounter_phase',
      key: 'encounter_phase',
    },
    {
      title: <span className="font-[700]"> Encounter Type </span>,
      dataIndex: 'encounter_type',
      key: 'encounter_type',
    },

    {
      title: '',
      key: 'actions',
      dataIndex: 'actions',
      render: (text, record) => {
        return (
          <Dropdown
            className=""
            overlay={menuOptions(record?.knowledgebase_id, record)}
          >
            <a
              className=""
              onClick={(e) => {
                e.preventDefault();
              }}
            >
              <ActionMenuIcon />
            </a>
          </Dropdown>
        );
      },
    },
  ];

  const initialFilterValue = {
    specialty_id: null,
  };

  const [filterValue, setFilterValue] = useState(initialFilterValue);

  const filterHandler = (type, value) => {
    if (type === 'specialty' && selectedOrganizationUUID) {
      let filters = {};
      if (value === 'All') {
        setFilterValue({
          ...filterValue,
          specialty_id: null,
        });
        setIsFilterOn(false);
        filters = {};
      } else if (value) {
        setFilterValue({
          ...filterValue,
          specialty_id: value,
        });
        setIsFilterOn(true);
        filters = { specialty_id: value };
      } else {
        setFilterValue({
          ...filterValue,
          specialty_id: null,
        });
        setIsFilterOn(false);
        filters = {};
      }
      dispatch(
        listKnowledgebaseOverridesAction(selectedOrganizationUUID, filters)
      );
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row lg:justify-between justify-center items-center mb-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="bg-white shadow-sm rounded-xl w-60 flex items-center">
            <Select
              style={{
                placeholder: {
                  fontFamily: 'Poppins',
                  color: '#667085',
                },
              }}
              bordered={false}
              className="border-none font-sans h-10 flex items-center focus:outline-none w-full"
              optionFilterProp="label"
              placeholder="Select specialty"
              showSearch
              allowClear
              options={[
                { label: 'All', value: 'All' },
                ...(specialties?.map((spec) => ({
                  label: spec.specialty_name,
                  value: spec.specialty_id,
                })) || []),
              ]}
              onChange={(e) => filterHandler('specialty', e)}
              value={isfilterOn ? filterValue.specialty_id : 'All'}
              id="Specialty_Id"
            ></Select>
          </div>
        </div>
        <div className="flex justify-end items-end">
          <Button
            type={'button'}
            onClick={() => {
              if (selectedOrganizationUUID) {
                setKnowledgebaseModalOpen(true);
              }
            }}
            disabled={!selectedOrganizationUUID}
          >
            <PlusIcon className={`mr-2`} /> Add knowledge base override
          </Button>
        </div>
      </div>
      <div>
        <div className="mt-5 ant-table rounded-xl shadow-xl">
          <SuperAdminTable
            className={'appointment-page-table'}
            data={tableData}
            columns={columns}
            loading={knowledgebaseOverridesLoading}
          />
        </div>
      </div>

      <AddNewKnowledgeBaseOverrides
        isOpen={knowledgebaseModalOpen}
        setIsOpen={setKnowledgebaseModalOpen}
        isfilterOn={isfilterOn}
        filterValue={filterValue}
        orgUuid={selectedOrganizationUUID}
      />

      <EditKnowledgeBaseOverrides
        isOpen={editModalOpen}
        setIsOpen={setEditModalOpen}
        knowledgebaseId={editingKnowledgebaseId}
        isfilterOn={isfilterOn}
        filterValue={filterValue}
        orgUuid={selectedOrganizationUUID}
      />

      <DeleteKnowledgeBaseOverrides
        isOpen={deleteModalOpen}
        setIsOpen={setDeleteModalOpen}
        knowledgebaseId={deletingKnowledgebaseId}
        filterValue={filterValue}
        orgUuid={selectedOrganizationUUID}
      />
    </div>
  );
};

export default KnowledgeBaseOverridesPage;
